// Categories + attributes editor (admin console, route /categories) — the
// write side of the catalog tree. The read hook (`useAdminCategories`) and
// its cache key already live in useAdminCatalog.js; this file adds only the
// mutations, keeping that file product-focused. The key is the same array
// literal `['admin', 'catalogCategories']` — this repo's hook files agree on
// key values, they don't import a shared constant across files (see
// useAdminUsers.js's own note).
//
// Deletion rule (root CLAUDE.md / this screen's brief): anything in use is
// DEACTIVATED, never hard-deleted. The DELETE endpoint exists only for a
// genuinely empty category (no products, no children) and the backend
// enforces that (CategoryHasDependentsError) — the UI offers Delete only as
// the exception, Deactivate as the default.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from 'shared/api/client.js';

const CATEGORIES_KEY = ['admin', 'catalogCategories'];
const attributesKey = (categoryId) => ['admin', 'categoryOwnAttributes', categoryId ?? null];

function invalidateCategories(queryClient) {
  queryClient.invalidateQueries({ queryKey: CATEGORIES_KEY });
}

// POST /admin/catalog/categories — { name, nameAm?, parentId?, sortOrder? }
export function useCreateCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) => apiClient.post('/admin/catalog/categories', body),
    onSuccess: () => invalidateCategories(queryClient),
  });
}

// PATCH /admin/catalog/categories/:id — partial. Covers rename (name /
// nameAm), reorder (sortOrder), and move in the tree (parentId, including
// `null` to move back to the top level).
export function useUpdateCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }) => apiClient.patch(`/admin/catalog/categories/${id}`, patch),
    onSuccess: () => invalidateCategories(queryClient),
  });
}

// POST /admin/catalog/categories/:id/deactivate — the default "remove"
// action for a category that has products or children.
export function useDeactivateCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => apiClient.post(`/admin/catalog/categories/${id}/deactivate`),
    onSuccess: () => invalidateCategories(queryClient),
  });
}

// DELETE /admin/catalog/categories/:id — only succeeds for an empty
// category; the backend throws CategoryHasDependentsError otherwise (mapped
// to error.CATEGORY_HAS_DEPENDENTS for the caller).
export function useDeleteCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => apiClient.delete(`/admin/catalog/categories/${id}`),
    onSuccess: () => invalidateCategories(queryClient),
  });
}

// GET /admin/catalog/categories/:id/attributes — attributes defined
// DIRECTLY on this category (both is_active states), not the inherited
// effective set (that's useEffectiveAttributes in useAdminCatalog.js, for
// the product form). Disabled until a category is selected.
export function useCategoryAttributes(categoryId) {
  return useQuery({
    queryKey: attributesKey(categoryId),
    queryFn: () => apiClient.get(`/admin/catalog/categories/${categoryId}/attributes`),
    select: (data) => data.attributes,
    enabled: Boolean(categoryId),
  });
}

// POST /admin/catalog/categories/:id/attributes — { attributeKey, label,
// labelAm?, inputType, options?, required?, sortOrder? }
export function useCreateCategoryAttribute(categoryId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      apiClient.post(`/admin/catalog/categories/${categoryId}/attributes`, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: attributesKey(categoryId) }),
  });
}

// PATCH /admin/catalog/attributes/:attributeId — partial (label, labelAm,
// inputType, options, required, sortOrder; attribute_key is immutable).
export function useUpdateCategoryAttribute(categoryId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ attributeId, patch }) =>
      apiClient.patch(`/admin/catalog/attributes/${attributeId}`, patch),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: attributesKey(categoryId) }),
  });
}

// POST /admin/catalog/attributes/:attributeId/deactivate
export function useDeactivateCategoryAttribute(categoryId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (attributeId) =>
      apiClient.post(`/admin/catalog/attributes/${attributeId}/deactivate`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: attributesKey(categoryId) }),
  });
}
